/**
 * Generisches CSV-Parsing fuer den Broker-Import (backlog.md -> Punkt 19/C1). Erkennt
 * Trennzeichen (Komma/Semikolon) und Zahlenformat automatisch, da deutsche
 * Broker-Exporte i.d.R. Semikolon+Komma nutzen, waehrend manche internationale Tools
 * Komma+Punkt nutzen. Reine Funktionen, kein I/O.
 */

export function detectDelimiter(headerLine: string): "," | ";" {
  const semicolons = (headerLine.match(/;/g) ?? []).length;
  const commas = (headerLine.match(/,/g) ?? []).length;
  return semicolons >= commas ? ";" : ",";
}

/**
 * Einfaches CSV-Zeilen-Parsing mit Unterstuetzung fuer in Anfuehrungszeichen gesetzte
 * Felder (koennen das Trennzeichen selbst enthalten) - kein vollstaendiger
 * RFC4180-Parser (z.B. keine mehrzeiligen Quoted-Felder), reicht aber fuer die hier
 * bekannten Broker-Exporte.
 */
export function parseCsvLine(line: string, delimiter: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      fields.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
}

export function parseCsvText(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const delimiter = detectDelimiter(lines[0]);
  const [headerLine, ...rest] = lines;
  const headers = parseCsvLine(headerLine, delimiter).map((h) => h.trim());
  const rows = rest.map((line) => parseCsvLine(line, delimiter));
  return { headers, rows };
}

/**
 * Deutsches Zahlenformat (Punkt = Tausendertrenner, Komma = Dezimaltrenner) UND
 * internationales Format (Komma = Tausendertrenner, Punkt = Dezimaltrenner) - erkennt
 * anhand des LETZTEN Trenn-Zeichens im String, welches der Dezimaltrenner ist (der
 * Dezimaltrenner steht immer naeher am Ende der Zahl als ein Tausendertrenner).
 */
export function parseFlexibleNumber(raw: string): number | undefined {
  const trimmed = raw.trim().replace(/[€$\s]/g, "");
  if (trimmed === "") return undefined;

  const lastComma = trimmed.lastIndexOf(",");
  const lastDot = trimmed.lastIndexOf(".");

  let normalized: string;
  if (lastComma > lastDot) {
    normalized = trimmed.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = trimmed.replace(/,/g, "");
  } else {
    normalized = trimmed;
  }

  const value = Number(normalized);
  return Number.isFinite(value) ? value : undefined;
}

/** Deutsches (TT.MM.JJJJ) und ISO-Datum (JJJJ-MM-TT...) - beides bei den
 * recherchierten Broker-Formaten vorkommend. Gibt IMMER ISO (JJJJ-MM-TT) zurueck. */
export function parseFlexibleDate(raw: string): string | undefined {
  const trimmed = raw.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const german = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(trimmed);
  if (german) return `${german[3]}-${german[2]}-${german[1]}`;
  return undefined;
}
